import CloseIcon from '@mui/icons-material/Close';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import { Box, Fade, IconButton, Portal } from '@mui/material';
import { FC, ReactNode, useEffect } from 'react';

const DEFAULT_AUTO_HIDE_MS = 20000;

type NotificationProps = {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
  icon?: ReactNode;
  accentColor: string;
  /** Auto-dismiss after N ms. Pass 0 or null to disable. Defaults to 6000ms. */
  autoHideMs?: number | null;
};

const Notification: FC<NotificationProps> = ({
  open,
  onClose,
  children,
  icon,
  accentColor,
  autoHideMs = DEFAULT_AUTO_HIDE_MS,
}: NotificationProps) => {
  useEffect(() => {
    if (!open || !autoHideMs) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      onClose();
    }, autoHideMs);
    return () => window.clearTimeout(timeoutId);
  }, [open, autoHideMs, onClose]);

  if (!open) {
    return null;
  }

  return (
    <Fade in={open} timeout={{ enter: 250, exit: 250 }}>
      <Box
        onClick={onClose}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          padding: '16px',
          minWidth: '320px',
          maxWidth: '400px',
          border: '1px solid #e0e0e0',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: accentColor,
            borderRadius: '12px 0 0 12px',
          }}
        />
        <Box
          sx={{
            backgroundColor: `${accentColor}20`,
            borderRadius: '50%',
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ml: 1,
          }}>
          {icon || <VoicemailIcon sx={{ color: accentColor, fontSize: 24 }} />}
        </Box>

        <Box sx={{ flex: 1 }}>{children}</Box>
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#999',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
    </Fade>
  );
};

type NotificationStackProps = {
  children: ReactNode;
};

export const NotificationStack: FC<NotificationStackProps> = ({ children }) => {
  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          top: 64,
          right: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          zIndex: 1400,
        }}>
        {children}
      </Box>
    </Portal>
  );
};

export default Notification;
